<template>
    <div class="best-performer-card rounded-xl ml-4" :style="{ '--team-color': teamColor }">
        <div class="best-performer-photo">
            <img
                :src="`${assetsUrl}/${player.image}`"
                height="185px"
                width="auto"
                @error="$event.target.src = defaultPlayerImg"
            >
        </div>
        <div class="best-performer-info">
            <div class="best-performer-header">
                <div class="best-performer-title custom-orange-text font-weight-bold">
                    Best Performer
                </div>
                <div class="best-performer-period">{{ periodLabel }}</div>
            </div>
            <div class="best-performer-divider custom-orange" />
            <div class="best-performer-name">
                <img
                    v-if="team && team.logo"
                    :src="`${assetsUrl}/${team.logo}`"
                    class="best-performer-team-logo"
                >
                <span v-if="player.jersey !== undefined && player.jersey !== null" class="best-performer-jersey custom-orange-text">#{{ player.jersey }}</span>
                <span class="best-performer-name-text">{{ player.name }} {{ player.surname }}</span>
            </div>
            <div class="best-performer-stats">
                <div v-for="stat in statList" :key="stat.label" class="best-performer-stat" :class="{ 'best-performer-stat-highlight custom-orange': stat.highlight }">
                    <div class="best-performer-stat-value">{{ stat.value }}</div>
                    <div class="best-performer-stat-label" :class="{ 'custom-orange-text': !stat.highlight }">{{ stat.label }}</div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import { BACKGROUNDS, matchColor, readableOn } from '@/utils/colors'

    export default {
        name: 'BestPerformerCard',
        props: {
            player: {
                type: Object,
                required: true
            },
            team: {
                type: Object,
                default: null
            },
            stats: {
                type: Object,
                required: true
            },
            period: {
                type: Number,
                required: true
            }
        },
        data() {
            return {
                assetsUrl: 'https://storage.googleapis.com',
                defaultPlayerImg: require('@/assets/default_player.png')
            }
        },
        computed: {
            teamColor() {
                // lightened if it would vanish on the black card
                return readableOn(matchColor(this.team) || '#ff7133', BACKGROUNDS.black)
            },
            periodLabel() {
                const quarters = { 1: '1st Period', 2: '2nd Period', 3: '3rd Period', 4: '4th Period' }
                if (quarters[this.period]) return quarters[this.period]
                const overtime = this.period - 4
                return overtime === 1 ? 'Overtime' : `Overtime ${overtime}`
            },
            statList() {
                return [
                    { label: 'PTS', value: this.stats.points },
                    { label: 'REB', value: this.stats.rebounds },
                    { label: 'AST', value: this.stats.assists },
                    { label: 'STL', value: this.stats.steals },
                    { label: 'BLK', value: this.stats.blockedShots },
                    { label: 'PIR', value: this.stats.pir, highlight: true }
                ]
            }
        }
    }
</script>

<style scoped>
.best-performer-card {
    display: flex;
    align-items: stretch;

    /* Grows with the player's name instead of cutting it (capped by the screen edge) */
    width: max-content;
    min-width: 680px;
    max-width: calc(100% - 16px);
    min-height: 200px;
    background-color: black;
    color: white;
    font-family: Arial, sans-serif;
    overflow: hidden;
    border-left: 8px solid var(--team-color); /* same accent as the score column */
}

.best-performer-photo {
    position: relative;
    flex: 0 0 190px;
    display: flex;
    align-items: flex-end;
    justify-content: center;
    background: radial-gradient(circle at 50% 70%, var(--team-color) 0%, transparent 70%);
}

.best-performer-photo img {
    display: block;
    max-width: 100%;
    object-fit: contain;
    object-position: bottom;
}

.best-performer-info {
    flex: 1 1 auto;
    min-width: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 0 20px 0 12px;
}

.best-performer-header {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
}

.best-performer-title {
    font-size: 1.6rem;
    text-transform: uppercase;
    letter-spacing: 1px;
}

.best-performer-period {
    font-size: 1.1rem;
    font-weight: bold;
    text-transform: uppercase;
    letter-spacing: 1px;
    color: rgba(255, 255, 255, 0.85);
}

.best-performer-divider {
    height: 3px;
    margin: 6px 0 10px;
    border-radius: 2px;
}

.best-performer-name {
    display: flex;
    align-items: center;
    margin-bottom: 12px;
    font-size: 1.6rem;
    font-weight: bold;
    white-space: nowrap;
}

.best-performer-team-logo {
    width: 38px;
    height: auto;
    margin-right: 10px;
}

.best-performer-jersey {
    margin-right: 8px;
}

/* a name that does not fit the screen goes on to a second line, never cut */
.best-performer-name-text {
    text-align: left;
    white-space: normal;
    overflow-wrap: anywhere;
    line-height: 1.15;
}

.best-performer-stats {
    display: flex;
    gap: 6px;
}

.best-performer-stat {
    flex: 1 1 0;
    text-align: center;
    padding: 6px 0 4px;
    border-radius: 10px;
    background-color: rgba(255, 255, 255, 0.1);
}

.best-performer-stat-highlight {
    flex-grow: 1.3;
}

.best-performer-stat-value {
    font-size: 2rem;
    font-weight: bold;
    line-height: 1.1;
}

.best-performer-stat-label {
    font-size: 0.85rem;
    font-weight: bold;
    letter-spacing: 1px;
}

.best-performer-stat-highlight .best-performer-stat-label {
    color: white;
}
</style>
